import { computed, inject, Injectable } from '@angular/core';
import { ToolsService } from 'src/app/services';
import { RegisterUserService } from './register-user.service';
import { RecommendedRouteToken } from './recommended-route-token';

/**
 * HRS-2990: qué campos del formulario unificado se muestran y cuáles se exigen,
 * según el modo del informador. Antes esto vivía repartido en cuatro sitios
 * (plantilla, factory de formulario, validación y componente) como negaciones de
 * `isTechMode`, y cada flujo nuevo obligaba a tocar los cuatro.
 *
 * Dos ejes independientes:
 * - `show*`: si el bloque se pinta.
 * - `require*`: si además es obligatorio.
 *
 * Son ejes distintos porque con `recommendedRouteToken` (el visitante que parte
 * de una ruta recomendada) los intereses se muestran pero no se exigen: la
 * plantilla ya trae los suyos.
 */
@Injectable({ providedIn: 'root' })
export class InformerFormPolicy {
  private _toolsService = inject(ToolsService);
  private _registerUserService = inject(RegisterUserService);
  private _recommendedRouteTokenService = inject(RecommendedRouteToken);

  /** `null` = visitante normal (planner público). */
  public mode = this._toolsService.informerMode;

  private _recommendedRouteToken = this._recommendedRouteTokenService.recommendedRouteToken;

  // D6: un tenant puede no traer tipos de viajero o categorías. Si la lista
  // llega vacía se oculta el bloque y no se exige, en vez de dejar un formulario
  // imposible de enviar (era el estado del planner público en esos tenants).
  private _hasTripTypes = computed(() => (this._registerUserService.registerUser()?.travelerType?.length ?? 0) > 0);
  private _hasCategories = computed(() => (this._registerUserService.registerUser()?.categories?.length ?? 0) > 0);

  /** El informador no ve el «Planifica tu viaje a X»: no está planificando el suyo. */
  public showTitle = computed(() => this.mode() === null);

  /** El registro simple no pasa por el planner, así que no hay ruta que situar. */
  public showStartLocation = computed(() => this.mode() !== 'simple');

  /**
   * La hora de inicio solo sirve para situar el arranque de una ruta: el planner del
   * visitante, el registro completo (que instancia la ruta predefinida del correo) y
   * el modo de ruta predefinida. El registro simple no crea ninguna, así que no se
   * pregunta.
   */
  public showStartTime = computed(() => this.mode() !== 'simple');

  /**
   * El apartado «Estancia» agrupa hora de inicio y punto de partida: si no se pinta
   * ninguno de los dos, el titular sobra (es el caso del registro simple).
   */
  public showStay = computed(() => this.showStartTime() || this.showStartLocation());

  /**
   * El radio solo tiene sentido cuando hay una ruta que calcular: el planner del
   * visitante y el modo de ruta predefinida. En los registros del informador no se
   * planifica nada, así que no se pregunta (el simple ya lo tenía fuera).
   *
   * HRS-2579: además, cada app decide desde el CMS si se pregunta. Sin el bloque
   * (apps anteriores, o el CMS aún sin contestar) no se pinta: el planner sigue
   * acotando con los 75 km de siempre, que es lo que el CMS manda en `defaultKm`.
   */
  public showSearchRadius = computed(() =>
    (this.mode() === null || this.mode() === 'predefined-route')
    && !this._recommendedRouteToken()
    && this._toolsService.maxRouteRadius()?.enabled === true
  );

  /** Público / Solo QR: solo tiene sentido sobre una ruta predefinida. */
  public showPrivacy = computed(() => this.mode() === 'predefined-route');

  public showTripTypes = this._hasTripTypes;
  public showInterests = this._hasCategories;

  public requireTripTypes = computed(() => this.showTripTypes() && !this._recommendedRouteToken());
  public requireInterests = computed(() => this.showInterests() && !this._recommendedRouteToken());

  /**
   * HRS-3088: cuántos intereses hay que marcar. Los tres son del planner —el del
   * visitante y el de la ruta predefinida—, donde alimentan el cálculo de la ruta y
   * el mapa. Los registros del técnico de turismo no planifican nada: allí basta
   * uno, que es lo que pedía su paso a paso propio antes de unificarse (HRS-2990).
   */
  public minInterests = computed(() => this.mode() === 'simple' || this.mode() === 'complete' ? 1 : 3);

  /** Los campos configurados en el CMS son del registro del visitante, no de la ruta. */
  public showModularFields = computed(() => this.mode() !== 'predefined-route' && !this._recommendedRouteToken());
}
